import { getAttendanceStatusLabel } from './attendanceLabels';

export interface EmployeeDashboardGroup {
  id: string;
  employeeId: string;
  agentName: string;
  attendanceStatus: string;
  attendanceLabel: string;
  lateMinutes: number;
  clockInTime: string | null;
  clockOutTime: string | null;
  coverageStatus: string;
  coverageLabel: string;
  assignments: any[];
  hasMultipleAssignments: boolean;
  isCoveringOthers: boolean;
  coverageMode: string | null;
  coverageAssignmentId: string | null;
  requiresCoverage: boolean;
  primaryRow: any;
}

function normalizeTime(value?: string | null): string {
  if (!value) return '';
  return value.slice(0, 5);
}

function sortAssignments(assignments: any[]): any[] {
  return [...assignments].sort((a, b) => {
    const startA = normalizeTime(a.scheduledStartTime);
    const startB = normalizeTime(b.scheduledStartTime);
    if (startA !== startB) return startA.localeCompare(startB);
    return (a.campaignName || '').localeCompare(b.campaignName || '');
  });
}

export function resolveEmployeeAttendanceLabel(assignments: any[]): {
  attendanceStatus: string;
  attendanceLabel: string;
  lateMinutes: number;
} {
  const hasClockIn = assignments.some((row) => Boolean(row.clockInTime));
  const isLate = assignments.some((row) => ['LATE', 'SLIGHTLY_LATE'].includes(row.attendanceStatus));
  const lateMinutes = Math.max(0, ...assignments.map((row) => row.lateMinutes || 0));

  if (hasClockIn && isLate) {
    return { attendanceStatus: 'LATE', attendanceLabel: 'Present (Late)', lateMinutes };
  }

  if (hasClockIn) {
    return { attendanceStatus: 'PRESENT', attendanceLabel: 'Present', lateMinutes: 0 };
  }

  const priority = ['NO_CALL_NO_SHOW', 'ABSENT', 'SCHEDULED_OFF', 'EXPECTED', 'PRESENT', 'LATE', 'SLIGHTLY_LATE'];
  const status = priority.find((value) => assignments.some((row) => row.attendanceStatus === value))
    || assignments[0]?.attendanceStatus
    || 'EXPECTED';

  if (status === 'NO_CALL_NO_SHOW') {
    return { attendanceStatus: status, attendanceLabel: 'No Show', lateMinutes: 0 };
  }

  return {
    attendanceStatus: status,
    attendanceLabel: getAttendanceStatusLabel(status),
    lateMinutes: 0,
  };
}

export function resolveEmployeeCoverageLabel(assignments: any[]): {
  coverageStatus: string;
  coverageLabel: string;
  coverageMode: string | null;
} {
  if (assignments.some((row) => row.coverageMode === 'FULL_TRANSFER')) {
    return { coverageStatus: 'TRANSFERRED', coverageLabel: 'Transferred', coverageMode: 'FULL_TRANSFER' };
  }
  if (assignments.some((row) => row.isCoveringOthers)) {
    return { coverageStatus: 'COVERING', coverageLabel: 'Covering', coverageMode: assignments.find((r) => r.isCoveringOthers)?.coverageMode || null };
  }
  if (assignments.some((row) => row.coverageStatus === 'PARTIALLY_COVERED')) {
    return { coverageStatus: 'PARTIALLY_COVERED', coverageLabel: 'Partial Coverage', coverageMode: null };
  }
  if (assignments.some((row) => row.coverageStatus === 'COVERED')) {
    return { coverageStatus: 'COVERED', coverageLabel: 'Covered', coverageMode: null };
  }
  if (assignments.some((row) => row.coverageStatus === 'NOT_COVERED' && row.requiresCoverage)) {
    return { coverageStatus: 'NOT_COVERED', coverageLabel: 'No Coverage', coverageMode: null };
  }
  if (assignments.some((row) => row.attendanceStatus === 'EXPECTED' && !row.requiresCoverage)) {
    return { coverageStatus: 'NOT_REQUIRED', coverageLabel: 'Awaiting Shift', coverageMode: null };
  }
  if (assignments.every((row) => row.coverageStatus === 'NOT_REQUIRED')) {
    return { coverageStatus: 'NOT_REQUIRED', coverageLabel: 'Not Required', coverageMode: null };
  }
  return {
    coverageStatus: assignments[0]?.coverageStatus || 'NOT_REQUIRED',
    coverageLabel: (assignments[0]?.coverageStatus || 'NOT_REQUIRED').replace(/_/g, ' '),
    coverageMode: null,
  };
}

function pickPrimaryRow(assignments: any[]): any {
  return assignments.find((row) => row.coverageStatus === 'NOT_COVERED' && row.requiresCoverage)
    || assignments.find((row) => row.coverageAssignmentId)
    || assignments[0];
}

export function groupDashboardRowsByEmployee(rows: any[]): EmployeeDashboardGroup[] {
  const byEmployee = new Map<string, any[]>();
  rows.forEach((row) => {
    const list = byEmployee.get(row.employeeId) || [];
    list.push(row);
    byEmployee.set(row.employeeId, list);
  });

  return Array.from(byEmployee.entries()).map(([employeeId, employeeRows]) => {
    const assignments = sortAssignments(employeeRows);
    const attendance = resolveEmployeeAttendanceLabel(assignments);
    const coverage = resolveEmployeeCoverageLabel(assignments);
    const primaryRow = pickPrimaryRow(assignments);
    const first = assignments[0];

    return {
      id: employeeId,
      employeeId,
      agentName: first.agentName,
      attendanceStatus: attendance.attendanceStatus,
      attendanceLabel: attendance.attendanceLabel,
      lateMinutes: attendance.lateMinutes,
      clockInTime: assignments.find((row) => row.clockInTime)?.clockInTime || null,
      clockOutTime: assignments.find((row) => row.clockOutTime)?.clockOutTime || null,
      coverageStatus: coverage.coverageStatus,
      coverageLabel: coverage.coverageLabel,
      assignments,
      hasMultipleAssignments: assignments.length > 1,
      isCoveringOthers: assignments.some((row) => row.isCoveringOthers),
      coverageMode: coverage.coverageMode,
      coverageAssignmentId: primaryRow?.coverageAssignmentId || null,
      requiresCoverage: assignments.some((row) => row.requiresCoverage),
      primaryRow,
    };
  }).sort((a, b) => a.agentName.localeCompare(b.agentName));
}

export function countDistinctEmployees(rows: any[]): number {
  return new Set(rows.map((row) => row.employeeId)).size;
}

export function formatAssignmentWindow(row: any): string {
  const start = normalizeTime(row.scheduledStartTime);
  const end = normalizeTime(row.scheduledEndTime);
  if (!start && !end) return '—';
  return `${start}–${end}`;
}

export function getEmployeeGroupExpanded(
  expanded: Record<string, boolean>,
  groupId: string,
  defaultOpen: boolean,
): boolean {
  return groupId in expanded ? expanded[groupId] : defaultOpen;
}

export function toggleEmployeeGroupExpanded(
  expanded: Record<string, boolean>,
  groupId: string,
  defaultOpen: boolean,
): Record<string, boolean> {
  const current = getEmployeeGroupExpanded(expanded, groupId, defaultOpen);
  return { ...expanded, [groupId]: !current };
}
